import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL, ApiResponse } from '../config/api.config';
import { ApiSessionService } from './api-session.service';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { SalesOrderService } from './sales-order.service';
import { PurchaseOrderService } from './purchase-order.service';
import { AccountsReceivableService } from './accounts-receivable.service';
import { AccountsPayableService } from './accounts-payable.service';
import { NotificationItem } from '../models/notification.model';
import { SupplyService } from './supply.service';
import { ProductService } from './product.service';
import { REFERENCE_TODAY } from '../utils/aging.util';

/**
 * Notifications are derived from real order/ledger state (flagged orders,
 * overdue receivables/payables) rather than a separate mock feed, so they
 * can never drift out of sync with the data that produced them. Read state
 * is stored per user in the API so the Topbar bell and dropdown share it.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly accountsReceivableService = inject(AccountsReceivableService);
  private readonly accountsPayableService = inject(AccountsPayableService);
  private readonly supplyService = inject(SupplyService);
  private readonly productService = inject(ProductService);

  private readonly http = inject(HttpClient);
  private readonly readIds$ = new BehaviorSubject<Set<string>>(new Set());
  private readLoaded = false;

  constructor() {
    inject(ApiSessionService).cleared$.subscribe(() => {
      this.readLoaded = false;
      this.readIds$.next(new Set());
    });
  }

  getAll(): Observable<NotificationItem[]> {
    this.loadReadState();
    return combineLatest([
      this.salesOrderService.getAll(),
      this.purchaseOrderService.getAll(),
      this.accountsReceivableService.getLedger(),
      this.accountsPayableService.getLedger(),
      this.readIds$,
      this.supplyService.getAll(),
      this.productService.getAll(),
    ]).pipe(
      map(([salesOrders, purchaseOrders, arLedger, apLedger, readIds, supplies, products]) => {
        const items: Omit<NotificationItem, 'read'>[] = [];

        // Tracked supplies at or below their reorder point (the key changes with the status, so "Out" notifies again).
        const productById = new Map(products.map((p) => [p.id, p]));
        for (const level of supplies) {
          const product = productById.get(level.productId);
          if (!product || level.status === 'OK') continue;
          items.push({
            id: `supply-${level.status.toLowerCase()}-${level.productId}`,
            icon: 'package',
            title: level.status === 'Out' ? 'Supply out of stock' : 'Supply running low',
            description: `${product.name} · ${level.available.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${product.unit} available`,
            date: REFERENCE_TODAY,
            link: ['/warehouse/supplies'],
          });
        }

        for (const order of salesOrders) {
          if (!order.flagged) continue;
          items.push({
            id: `flag-so-${order.id}`,
            icon: 'flag',
            title: 'Sales Order flagged',
            description: `${order.number} · ${order.flagReason ?? 'No reason given'}`,
            date: order.flaggedAt ?? order.orderDate,
            link: ['/orders/sales-orders', order.id],
          });
        }

        for (const order of purchaseOrders) {
          if (!order.flagged) continue;
          items.push({
            id: `flag-po-${order.id}`,
            icon: 'flag',
            title: 'Purchase Order flagged',
            description: `${order.number} · ${order.flagReason ?? 'No reason given'}`,
            date: order.flaggedAt ?? order.orderDate,
            link: ['/orders/purchase-orders', order.id],
          });
        }

        for (const row of arLedger) {
          if (row.status === 'Paid' || row.dueDate >= REFERENCE_TODAY) continue;
          items.push({
            id: `ar-${row.id}`,
            icon: 'alert-triangle',
            title: 'Invoice overdue',
            description: `${row.customerName} · ${row.number} · $${row.remainingBalance.toFixed(0)} due`,
            date: row.dueDate,
            link: ['/orders/sales-orders', row.id],
          });
        }

        for (const row of apLedger) {
          if (!row.isPayable || row.remainingBalance <= 0 || row.dueDate >= REFERENCE_TODAY) continue;
          items.push({
            id: `ap-${row.id}`,
            icon: 'alert-triangle',
            title: 'Bill overdue',
            description: `${row.vendorName} · ${row.number} · $${row.remainingBalance.toFixed(0)} due`,
            date: row.dueDate,
            link: row.sourceType === 'purchase-order' ? ['/orders/purchase-orders', row.id] : ['/finance/expenses'],
          });
        }

        return items
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, 20)
          .map((item) => ({ ...item, read: readIds.has(item.id) }));
      }),
    );
  }

  markRead(id: string): void {
    if (this.readIds$.value.has(id)) return;
    this.markAllRead([id]);
  }

  markAllRead(ids: string[]): void {
    const unread = ids.filter((id) => !this.readIds$.value.has(id));
    if (!unread.length) return;
    this.readIds$.next(new Set([...this.readIds$.value, ...unread]));
    this.http.post<ApiResponse<string[]>>(`${API_URL}/notification-reads`, { keys: unread }).subscribe({
      error: (error) => console.error('[API] notification-reads', error),
    });
  }

  /** Read state is stored per user in the API (/notification-reads). */
  private loadReadState(): void {
    if (this.readLoaded) return;
    this.readLoaded = true;
    this.http.get<ApiResponse<string[]>>(`${API_URL}/notification-reads`).subscribe({
      next: (response) => this.readIds$.next(new Set([...this.readIds$.value, ...response.data])),
      error: (error) => {
        this.readLoaded = false;
        console.error('[API] notification-reads', error);
      },
    });
  }
}
